import { useState } from "react";
import { DownloadSimple, UploadSimple, X, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable import/export controls for an entity.
 * Props: entity, onImported()
 */
export default function ImportExportButtons({ entity, onImported, canImport = true }) {
  const [busy, setBusy] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const download = async (fmt) => {
    setBusy(true);
    try {
      const token = localStorage.getItem("access_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/imex/${entity}/export.${fmt}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = `${entity}.${fmt}`;
      document.body.appendChild(dl); dl.click(); dl.remove();
      toast.success(`Exported ${entity}.${fmt}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
    setBusy(false);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/imex/${entity}/import`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      toast.success(`Imported ${data.imported} rows (${data.skipped} skipped)`);
      onImported && onImported(data);
      setShowImport(false);
    } catch (e) {
      toast.error(`Import failed: ${String(e.message || e).slice(0, 120)}`);
    }
    setBusy(false);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <button onClick={() => download("csv")} disabled={busy} className="btn-ghost btn-sm" data-testid={`imex-export-csv-${entity}`}>
          <DownloadSimple size={13}/> CSV
        </button>
        <button onClick={() => download("xlsx")} disabled={busy} className="btn-ghost btn-sm" data-testid={`imex-export-xlsx-${entity}`}>
          <DownloadSimple size={13}/> Excel
        </button>
        {canImport && (
          <button onClick={() => setShowImport(true)} className="btn-ghost btn-sm" data-testid={`imex-import-${entity}`}>
            <UploadSimple size={13}/> Import
          </button>
        )}
      </div>
      {showImport && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={() => setShowImport(false)}>
          <div className="modal-surface modal-scale w-full max-w-md p-7" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow">Import {entity.replace(/_/g," ")}</div>
                <div className="display-3 mt-1">From CSV / Excel</div>
              </div>
              <button onClick={()=>setShowImport(false)} className="btn-ghost btn-icon"><X size={14}/></button>
            </div>
            <label className="upload-zone flex flex-col items-center gap-3 py-8 cursor-pointer">
              <UploadSimple size={26} className="text-[color:hsl(var(--tint-indigo))]"/>
              <div className="font-display font-semibold">Choose file</div>
              <div className="text-xs text-muted-foreground">CSV or .xlsx — first row must be headers</div>
              <input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e)=>upload(e.target.files?.[0])} data-testid={`imex-file-${entity}`}/>
            </label>
            <button
              onClick={() => download("csv")}
              className="btn-ghost w-full mt-4 btn-sm"
              data-testid={`imex-template-${entity}`}
            >
              <DownloadSimple size={12}/> Download current data as a template
            </button>
            {busy && <div className="mt-4 flex items-center justify-center text-xs text-muted-foreground"><ArrowsClockwise size={12} className="animate-spin mr-2"/> Working…</div>}
          </div>
        </div>
      )}
    </>
  );
}
